import React, { FC, useState } from 'react';
import {
	Box,
	Collapse,
	Flex,
	HStack,
	List,
	ListItem,
	ListIcon,
	Text,
} from 'src/components/ui';
import 'src/styles/hover.css';
import { IoChevronDownOutline, IoChevronUpOutline } from 'react-icons/io5';
import { usePopup } from 'src/contexts/PopupContext';
import { IMenuItem } from 'src/services/menu';
import { iconMapper } from 'src/services/iconMapper';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

const refreshRequiredUrls = ['/projects/campaigns/new'];

interface NestedNavListProps extends IMenuItem {
	shouldRefresh?: boolean;
	setDrawerOpen?: (open: boolean) => void;
	onItemClick?: () => void;
}

/**
 * Generate a menu ID from the label for tour targeting
 */
const getMenuIdFromLabel = (label: string): string | undefined => {
	// Map labels to menu IDs for onboarding tour (case-insensitive)
	const normalizedLabel = label.toLowerCase().trim();

	const labelToIdMap: Record<string, string> = {
		'home': 'menu-home',
		'ad campaigns': 'menu-campaigns',
		'campaigns': 'menu-campaigns',
		'tasks': 'menu-tasks',
		'quick tools': 'menu-tasks',
		'account': 'menu-account',
		'commerce': 'menu-commerce',
		'products': 'menu-commerce',
		'catalog': 'menu-catalog',
		'integrations': 'menu-integrations',
		'ai voice agent': 'menu-voice-agent',
	};

	return labelToIdMap[normalizedLabel];
};

/**
 * Generate a submenu ID from the label for guided cursor targeting
 */
const getSubmenuIdFromLabel = (label: string): string | undefined => {
	const labelToIdMap: Record<string, string> = {
		'Create Ads': 'submenu-create-ads',
		'Create Ad': 'submenu-create-ads',
		'New Campaign': 'submenu-create-ads',
		'All Campaigns': 'submenu-all-campaigns',
		'Campaigns': 'submenu-all-campaigns',
	};

	return labelToIdMap[label];
};

const NestedNavList: FC<NestedNavListProps> = ({
	label,
	icon,
	children,
	setDrawerOpen,
	onItemClick,
}) => {
	const { pathname } = useLocation();
	const [search] = useSearchParams();
	const navigate = useNavigate();
	const { setAdTemplateModalOpen } = usePopup();

	const [isExpanded, setIsExpanded] = useState<boolean>(() => {
		return children.some(
			(item) =>
				pathname === item.url ||
				(item.url && pathname.startsWith(`${item.url}/`)),
		);
	});

	// Listen for collapse all menus event (used by tour)
	React.useEffect(() => {
		const handleCollapseAll = () => {
			setIsExpanded(false);
		};

		window.addEventListener('collapseAllMenus', handleCollapseAll);
		return () => window.removeEventListener('collapseAllMenus', handleCollapseAll);
	}, []);

	const toggleMenu = () => {
		setIsExpanded((prev) => !prev);
	};

	const activeListProps = children.some(
		(item) =>
			pathname === item.url ||
			(item.url && pathname.startsWith(`${item.url}/`)),
	)
		? {
				color: 'orange.base',
				bg: '#EDEEEF',
				borderLeftRadius: 0,
			}
		: {};

	const activeLinkProps = {
		color: 'orange.base',
		bg: 'orange.lighter',
	};

	const category = search.get('category');

	const isCategoryActive = (link: string) => {
		if (pathname === '/projects/campaigns/new') return false;

		const isTasksLink = link === '/tasks';

		const isCatActive =
			(link &&
				(isTasksLink ? pathname === link : pathname.includes(link)) &&
				!(
					pathname.includes('/projects/campaigns/') &&
					link === '/projects/campaigns'
				) &&
				!(pathname.includes('tasks') && category)) ||
			(link && link.split('=').pop() === category);

		return isCatActive;
	};

	const handleNavigate = (link: string) => {
		if (setDrawerOpen) setDrawerOpen(false);

		if (refreshRequiredUrls.includes(link)) {
			navigate('/refresh-page', {
				state: { to: link },
			});
		} else {
			navigate(link);
		}

		// Call onItemClick to collapse sidebar on mobile
		onItemClick?.();
	};

	return (
		<Flex>
			<Box
				borderRight={
					children.some(
						(item) =>
							pathname === item.url ||
							(item.url && pathname.startsWith(`${item.url}/`)),
					)
						? '3px solid #F7480B'
						: '3px solid transparent'
				}
				borderRadius="0px 4px 4px 0px"
				height="48px"
			/>
			<Box w="full">
				<ListItem cursor="pointer">
					<HStack
						id={getMenuIdFromLabel(label)}
						className="container"
						_hover={{
							color: 'orange.base',
						}}
						p={3}
						borderRadius="base"
						pl="20px"
						onClick={toggleMenu}
						{...activeListProps}
					>
						{icon && (
							<ListIcon
								as={iconMapper[icon as keyof typeof iconMapper] ?? (() => null)}
							/>
						)}
						<Text
							className="text"
							flex={1}
							fontWeight="500"
							lineHeight="175%"
							letterSpacing="0.2px"
							{...activeListProps}
						>
							{label}
						</Text>

						<ListIcon
							color="#414141"
							as={isExpanded ? IoChevronUpOutline : IoChevronDownOutline}
						/>
					</HStack>
				</ListItem>
				<Collapse in={isExpanded}>
					<List>
						{children.map((item) => {
							if (item.label === 'Ad Templates') {
								return (
									<ListItem key={item._id} className="container" pl="30px">
										<HStack
											className="container"
											_hover={{
												color: 'orange.base',
												cursor: 'pointer',
											}}
											p={2}
											pl="20px"
											borderRadius="6px"
											onClick={() => {
												setAdTemplateModalOpen(true);
												if (setDrawerOpen) setDrawerOpen(false);
												onItemClick?.();
											}}
											{...(isCategoryActive(item.url ?? '')
												? activeLinkProps
												: {})}
										>
											<Text
												className="text"
												fontWeight={500}
												color={
													isCategoryActive(item.url ?? '') ? 'orange.base' : ''
												}
											>
												{item.label}
											</Text>
										</HStack>
									</ListItem>
								);
							} else if (item.children && item.children.length > 0) {
								return (
									<NestedNavList
										key={item._id}
										{...item}
										setDrawerOpen={setDrawerOpen}
										onItemClick={onItemClick}
									/>
								);
							} else {
								return (
									<ListItem
										key={item._id}
										className="container"
										pl="30px"
										cursor="pointer"
									>
										<HStack
											id={getSubmenuIdFromLabel(item.label)}
											className="container"
											_hover={{
												color: 'orange.base',
											}}
											p={2}
											pl="20px"
											borderRadius="6px"
											onClick={() => handleNavigate(item.url ?? '')}
											{...(isCategoryActive(item.url ?? '')
												? activeLinkProps
												: {})}
										>
											<Text
												className="text"
												fontWeight={500}
												color={
													isCategoryActive(item.url ?? '') ? 'orange.base' : ''
												}
											>
												{item.label}
											</Text>
										</HStack>
									</ListItem>
								);
							}
						})}
					</List>
				</Collapse>
			</Box>
		</Flex>
	);
};

export default NestedNavList;
